import { AccountFeatures, AppContext, Features } from '@lightspeed-invoicing/core-client/frontend'
import { isEqual } from "lodash"
import React, { Component } from 'react'
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl'
import { Link, Redirect } from 'react-router-dom'
import EventBus from '../../common/EventBus.js'
import GlobalHelper from "../../common/GlobalHelper.jsx"
import ConfirmButton from "../../common/button/confirmbutton.component.jsx"
import Icon from "../../common/icon/icon.component.jsx"
import EmailInput from '../../common/inputs/EmailInputComponent.tsx'
import PhoneNumberInput from '../../common/inputs/PhoneNumberInputComponent.tsx'
import IntegrationButtonWrapper from '../../common/rich-text/feature-protection-wrappers/integrationButtonWrapper.component'
import LoadingSpinner from '../../common/spinner/loading.component.jsx'
import Integrations from "../../integration/Integrations.jsx"
import LocationSelect from "../../location/locationselect.component.jsx"
import { RoleService } from "../../role/services"
import ShopifyAppContext from '../../shopify/shopifyappcontext.js'
import { getNylasVersion } from '../../utils/common/utils'
import PasswordInput from './PasswordInput'
import PasswordStrengthVerificationInput from './PasswordStrengthVerificationInput'

const nylasEmailProviderIconMapping = {
    'gmail': <img className={'mr-2'} src={'/images/gmail_logo.svg'} width={'30px'} />,
    'eas': <img className={'mr-2'} src={'/images/outlook_email_logo.svg'} width={'30px'} />,
    'outlook': <img className={'mr-2'} src={'/images/outlook_email_logo.svg'} width={'30px'} />,
    'yahoo': <img className={'mr-2'} src={'/images/yahoo_logo.svg'} width={'30px'} />,
    'icloud': <img className={'mr-2'} src={'/images/icloud_logo.svg'} width={'30px'} />,
}

class UserEditForm extends Component {

    constructor(props, context) {
        super(props, context);
        const navigatorLanguage = window.navigator.userLanguage || window.navigator.language;
        const userLanguage =
            AppContext.getAccount().attributes.defaultLanguage
            || AppContext.getUserLoggedIn().attributes.language
            || (GlobalHelper.getSupportedLocales().includes(navigatorLanguage) ? navigatorLanguage : GlobalHelper.getDefaultLocale())

        let locationId = ''
        if (AppContext.locations.length === 1) {
            locationId = AppContext.locations[0].id
        }

        this.state = {
            id: '',
            firstName: '',
            lastName: '',
            phoneNumber: '',
            isValidPhoneNumber: true,
            checkValidForm: false,
            passwordAgain: '',
            password: '',
            currentPassword: '',
            email: '',
            language: userLanguage,
            roleKey: '',
            allRoleKeys: [],
            deleteButton: '',
            outlookExternalId: '',
            recordInFullStoryUntilDate: '',
            locationId,
            passwordRules: '',
            needReload: false,
            bookingFeatureEnabled: AppContext.hasFeatureIntegration(Features.Booking),
            cancelRedirectPath: this.props.cancelRedirectPath || '/users/listing',
            saveRedirectPath: this.props.saveRedirectPath || '/users/listing',
            nylasUserIntegrations: [],
            disconnectingEmailAccount: false,
            //key: integrationKey, value: externalId
            externalIdsMapping: {},
            //key: integrationKey, value: array of external users
            externalUsersByIntegrationMapping: {},
            loading: false,
            isPasswordAcceptable: false,
        }

        this.submitUser = this.submitUser.bind(this)
        this.handlePhoneNumberChange = this.handlePhoneNumberChange.bind(this)
        this.validateUserForm = this.validateUserForm.bind(this)
        this.handleFormChange = this.handleFormChange.bind(this)
        this.deleteUser = this.deleteUser.bind(this)
        this.handleExternalUserChange = this.handleExternalUserChange.bind(this)
        this.getFormattedRoleName = this.getFormattedRoleName.bind(this)
    }

    componentDidMount() {
        this.setState({
            loading: true,
        }, () => {
            setTimeout(() => {
                this.setState({
                    loading: false
                })
            }, 5000)
        })

        const user = this.props.user
        if (user && user.id) {
            this.loadUser(user)
        } else {
            if (AppContext.isMultiLocation() && AppContext.locations.length > 0) {
                this.setState({
                    locationId: AppContext.locations[0].id,
                })
            }
        }

        RoleService.get().then(response => {
            const roles = response.data
            for (let role of roles) {
                this.state.allRoleKeys.push(role) 
            }
        }) 

        AppContext.getApiClient().get('/external-users').then(res => {
            if (!res.error) {
                let externalUsersByIntegrationMapping = {}
                for (let user of res.data) {
                    const integrationKey = user.integrationKey
                    if (integrationKey) {
                        if (!externalUsersByIntegrationMapping[integrationKey]) {
                            externalUsersByIntegrationMapping[integrationKey] = []
                        }
                        externalUsersByIntegrationMapping[integrationKey].push(user)
                    }
                }

                this.setState({
                    loading: false,
                    externalUsersByIntegrationMapping: externalUsersByIntegrationMapping,
                })
            }
        })
    }

    handlePhoneNumberChange(phoneNumber, isValidPhoneNumber) {
        this.setState({
            phoneNumber: phoneNumber,
            isValidPhoneNumber: isValidPhoneNumber
        })
    }

    loadUser(user) {
        const deleteButton = <ConfirmButton identifier={GlobalHelper.GetFormattedName(user)} onClick={this.deleteUser} className="btn btn-danger">
            <FormattedMessage
                id="user.form.edit.delete_btn"
                defaultMessage="Delete user"
            />
        </ConfirmButton>
        const link = user.attributes.externalLinks.filter(l => l.attributes.integrationKey === "outlook")

        let externalIdsMapping = {}
        for (let externalLink of user.attributes.externalLinks) {
            externalIdsMapping[externalLink.attributes.integrationKey] = externalLink.attributes.externalId
        }

        this.setState({
            id: user.id,
            firstName: user.attributes.firstName,
            lastName: user.attributes.lastName,
            isValidPhoneNumber: GlobalHelper.isValidPhoneNumberInput(user.attributes.phoneNumber),
            phoneNumber: user.attributes.phoneNumber,
            email: user.attributes.email,
            language: user.attributes.language,
            roleKey: user.attributes.role.attributes.key,
            locationId: user.attributes.locationId,
            recordInFullStoryUntilDate: user.attributes.recordInFullStoryUntilDate || '',
            bookingFeatureEnabled: AppContext.hasFeatureIntegration(Features.Booking),
            outlookExternalId: link.length > 0 ? link[0].attributes.externalId : '',
            deleteButton: deleteButton,
            externalIdsMapping: externalIdsMapping,
        }, () => {
            this.loadUserIntegrationConfigurations()
        })
    }

    loadUserIntegrationConfigurations() {
        const nylasKey = getNylasVersion()
        if (this.state.id) {
            this.setState({
                nylasUserIntegrations: AppContext.integrations.filter(
                    it => it.attributes.userId === this.state.id &&
                        it.attributes.integrationKey === nylasKey
                ),
            })
        }
    }

    refreshUser() {
        AppContext.getApiClient().get('/users/' + this.state.id).then(user => {
            this.loadUser(user)
        })
    }

    deleteUser(event) {
        const user = this.props.user
        AppContext.getApiClient().delete('/users/' + user.id).then(response => {
            if (!response.error) {
                this.setState({ formStatus: <Redirect to={{ pathname: '/users/listing' }} /> })
            }
        })
        event.preventDefault()
    }

    hasExternalIdsMappingChanged(user) {
        const originalExternalIdsMapping = {}
        for (let externalLink of user.attributes.externalLinks) {
            originalExternalIdsMapping[externalLink.attributes.integrationKey] = externalLink.attributes.externalId
        }
        return !isEqual(originalExternalIdsMapping, this.state.externalIdsMapping)
    }


    isFieldDirty(field, user) {
        switch (field) {
            case "roleKey":
                return user.attributes.role.attributes.key !== this.state[field]
            case "externalIdsMapping":
                return this.hasExternalIdsMappingChanged(user)
            default:
                return user.attributes[field] !== this.state[field]
        }

    }

    formIsDirty(user) {
        if (!user) return true
        return Object.keys(this.getUpdatePayload(user)).length !== 0
    }

    getUpdatePayload(user) {
        let updates = {}
        const allowedFields = [
            "firstName",
            "lastName",
            "phoneNumber",
            "email",
            "language",
            "roleKey",
            "locationId",
            "externalIdsMapping",
        ]

        for (let i = 0; i < allowedFields.length; i++) {
            let field = allowedFields[i]
            if (this.isFieldDirty(field, user)) {
                updates[field] = this.state[field]
            }
        }
        return updates
    }

    async submitUser(event) {
        event.preventDefault()
        this.setState({ checkValidForm: true })

        if (!this.validateUserForm() || !this.state.isValidPhoneNumber) {
            return
        }

        const user = this.props.user
        let request = ""
        let reload = false
        if (user && user.id) {
            const payload = this.getUpdatePayload(user)
            request = AppContext.getApiClient().patch('/users/' + user.id, payload)
            reload = true
        } else {
            request = AppContext.getApiClient().post('/users', {
                "firstName": this.state.firstName,
                "lastName": this.state.lastName,
                "phoneNumber": this.state.phoneNumber,
                "email": this.state.email,
                "language": this.state.language,
                "password": this.state.password,
                "passwordAgain": this.state.passwordAgain,
                "roleKey": this.state.roleKey,
                "locationId": this.state.locationId,
                "externalIdsMapping": this.state.externalIdsMapping,
            })
        }

        let response = await request
        if (reload) {
            window.location.reload();
        }

        if (!response.error && !reload) {
            this.setState({ formStatus: <Redirect to={{ pathname: this.state.saveRedirectPath }} /> })
            EventBus.dispatch(EventBus.TopicUserCreated, {
                user: response,
            })
        }

        this.setState({ checkValidForm: false })

    }

    getFormattedRoleName(roleKey, messages){
        switch (roleKey){
            case "sales-man":
                return this.props.intl.formatMessage(messages["user.form.edit.role_sales_man"])
            case "admin":
                return this.props.intl.formatMessage(messages["user.form.edit.role_admin"])
            default:
                return roleKey 
        }
    }

    handleFormChange(event) {
        const target = event.target;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const name = target.name;

        this.setState({
            [name]: value
        });
    }

    deleteUserIntegration(integrationId) {
        return function (e) {
            e.preventDefault()
            this.setState({
                disconnectingEmailAccount: true,
            })
            AppContext.getApiClient().delete('/integration-configurations/' + integrationId).then(r => {
                this.refreshUser()
                this.setState({
                    disconnectingEmailAccount: false,
                }, () => {
                    if (GlobalHelper.getShopifyIntegrationOrigin()) {
                        ShopifyAppContext.redirectTo(window.location.pathname)
                    } else {
                        window.location.reload()
                    }
                })
            })
        }.bind(this)
    }

    validateUserForm() {
        let form = document.getElementById("user-form")
        const checkResult = form.checkValidity()
        form.classList.add('was-validated')
        return checkResult
    }

    handleExternalUserChange(event) {
        const target = event.target;
        const value = target.value;
        const name = target.name;

        let externalIdsMapping = Object.assign({}, this.state.externalIdsMapping)
        externalIdsMapping[name] = value

        this.setState({
            externalIdsMapping,
        });
    }

    render() {        
        if (this.state.needReload) {
            location.reload()
        }

        if (this.state.loading) {
            return <LoadingSpinner />
        }

        const messages = defineMessages({
            "user.form.edit.first_name_placeholder": {
                id: 'user.form.edit.first_name_placeholder',
                defaultMessage: 'First name of the user',
            },
            "user.form.edit.last_name_placeholder": {
                id: 'user.form.edit.last_name_placeholder',
                defaultMessage: 'Last name of the user',
            },
            "user.form.edit.email_placeholder": {
                id: 'user.form.edit.email_placeholder',
                defaultMessage: 'Email of the user',
            },
            "user.form.edit.phone_number_placeholder": {
                id: 'user.form.edit.phone_number_placeholder',
                defaultMessage: 'Phone number of the user',
            },
            "user.form.edit.password_placeholder": {
                id: 'user.form.edit.password_placeholder',
                defaultMessage: 'New password of the user',
            },
            "user.form.edit.current_password_placeholder": {
                id: 'user.form.edit.current_password_placeholder',
                defaultMessage: 'Current password of the user',
            },
            "user.form.edit.password_again_placeholder": {
                id: 'user.form.edit.password_again_placeholder',
                defaultMessage: 'Enter password again',
            },
            "user.form.edit.save_btn": {
                id: 'user.form.edit.save_btn',
                defaultMessage: 'Save user',
            },
            "user.form.edit.role_choose": {
                id: 'user.form.edit.role_choose',
                defaultMessage: 'Choose role',
            },
            "user.form.edit.role_admin": {
                id: 'user.form.edit.role_admin',
                defaultMessage: 'Admin',
            },
            "user.form.edit.role_sales_man": {
                id: 'user.form.edit.role_sales_man',
                defaultMessage: 'Salesperson',
            },
            "user.form.edit.language_english_us": {
                id: 'user.form.edit.language_english_us',
                defaultMessage: 'English US',
            },
            "user.form.edit.language_english_ca": {
                id: 'user.form.edit.language_english_ca',
                defaultMessage: 'English Canada',
            },
            "user.form.edit.language_french_ca": {
                id: 'user.form.edit.language_french_ca',
                defaultMessage: 'French Canada',
            },
            "user.form.edit.connect_email_account": {
                id: 'user.form.edit.connect_email_account',
                defaultMessage: 'Connect your email account',
            },
            "user.form.edit.none_label": {
                id: 'user.form.edit.none_label',
                defaultMessage: 'None',
            },
        })


        const nylasKey = getNylasVersion()
        const isAdmin = AppContext.getUserLoggedIn().attributes.role.attributes.key === "admin"
        const userIdOfUserBeingModified = this.state.id
        const nylasUrl = AppContext.getOptions().apiUrl + '/openid/connect/'+ nylasKey + '/' +
            userIdOfUserBeingModified + '?r=/users/edit/' + userIdOfUserBeingModified
        const canModifyEmailAccount = AppContext.getUserLoggedIn().id === userIdOfUserBeingModified || isAdmin
        return (
            <form className={"needs-validation"} onSubmit={this.submitUser} id={"user-form"} noValidate>
                <div className="row ui-has-modules">
                    <div className="col-12 ui-module">
                        <div className="form-group row">
                            <label className="col-2 col-form-label text-align-right" htmlFor="firstName">
                                <FormattedMessage
                                    id="user.form.edit.first_name_label"
                                    defaultMessage="First name"
                                />
                            </label>
                            <div className="col-10">
                                <input
                                    name="firstName"
                                    value={this.state.firstName}
                                    onChange={this.handleFormChange}
                                    id="firstName"
                                    type="text"
                                    className="form-control"
                                    placeholder={this.props.intl.formatMessage(messages['user.form.edit.first_name_placeholder'])}
                                />
                                <small className="form-text text-muted">
                                    <FormattedMessage
                                        id="user.form.edit.first_name_description"
                                        defaultMessage="First name of the user"
                                    />
                                </small>
                            </div>
                        </div>
                        <div className="form-group row">
                            <label className="col-2 col-form-label text-align-right" htmlFor="lastName">
                                <FormattedMessage
                                    id="user.form.edit.last_name_label"
                                    defaultMessage="Last name"
                                />
                            </label>
                            <div className="col-10">
                                <input
                                    name="lastName"
                                    value={this.state.lastName}
                                    onChange={this.handleFormChange}
                                    id="lastName"
                                    type="text"
                                    className="form-control"
                                    placeholder={this.props.intl.formatMessage(messages['user.form.edit.last_name_placeholder'])}
                                    required
                                />
                                <small className="form-text text-muted">
                                    <FormattedMessage
                                        id="user.form.edit.last_name_description"
                                        defaultMessage="Last name of the user"
                                    />
                                </small>
                            </div>
                        </div>
                        <div className="form-group row">
                            <label className="col-2 col-form-label text-align-right" htmlFor="email">
                                <FormattedMessage
                                    id="user.form.edit.email_label"
                                    defaultMessage="Email"
                                />
                            </label>
                            <div className="col-10">
                                <EmailInput
                                    email={this.state.email}
                                    name={"email"}
                                    id={"email"}
                                    type={"email"}
                                    className={"form-control"}
                                    handleFormChange={this.handleFormChange}
                                    required={false}
                                    placeholder={this.props.intl.formatMessage(messages['user.form.edit.email_placeholder'])}
                                />
                                <small className="form-text text-muted">
                                    <FormattedMessage
                                        id="user.form.edit.email_description"
                                        defaultMessage="Email of the user"
                                    />
                                </small>
                            </div>
                        </div>
                        {/* We will support only one email per user for the moment */}
                        {
                            canModifyEmailAccount && this.props.user && this.props.user.id && this.state.nylasUserIntegrations.length === 0 &&
                            <div className="form-group row pb-3">
                                <label className="col-2 col-form-label text-align-right">
                                </label>
                                <div className="col-10">
                                    <IntegrationButtonWrapper
                                        module={AccountFeatures.FeatureCustomEmail}
                                        redirectToAccount={true}
                                        to={nylasUrl}
                                        integrationKey={getNylasVersion()}
                                        className={"btn btn-outline-primary"}
                                        protectedClassName={"btn btn-outline-primary"}
                                        premium={false}
                                        protectionRule={AppContext.accountFeatureIsEnabled(AccountFeatures.FeatureCustomEmail)}
                                    >
                                        <span>
                                            <Icon value={'envelope'} className={'mr-2 fa-lg'} />
                                            {this.props.intl.formatMessage(messages['user.form.edit.connect_email_account'])}
                                        </span>
                                    </IntegrationButtonWrapper>

                                    <small className="form-text text-muted">
                                        <FormattedMessage
                                            id="user.form.edit.nylas_connect_description"
                                            defaultMessage="Connect your personal email account to send emails directly from your email address"
                                        />
                                    </small>
                                </div>
                            </div>
                        }
                        {canModifyEmailAccount && this.props.user && this.props.user.id && this.state.nylasUserIntegrations.length > 0 &&
                            this.state.nylasUserIntegrations.map(integration => {
                                return <div className="form-group row">
                                    <label className="col-2 col-form-label text-align-right">
                                    </label>
                                    <div className="col-10">
                                        <ConfirmButton
                                            className={'btn btn-outline-danger'}
                                            onClick={this.deleteUserIntegration(integration.id)}
                                        >
                                            {!this.state.disconnectingEmailAccount ?
                                                [
                                                    nylasEmailProviderIconMapping[integration.attributes.configuration.provider] || <Icon value={'envelope'} className={'mr-2 fa-lg'} />,
                                                    <FormattedMessage
                                                        id="user.form.edit.nylas_disconnect_btn_label"
                                                        defaultMessage="Disconnect {email}"
                                                        values={{
                                                            email: integration.attributes.configuration.email,
                                                        }}
                                                    />] : <LoadingSpinner height={20} width={3} className="px-5" />}
                                        </ConfirmButton>
                                        <small className="form-text text-muted">
                                            <FormattedMessage
                                                id="user.form.edit.nylas_disconnect_warning"
                                                defaultMessage="If you disconnect this email account, you will have to connect it again to send emails with it"
                                            />
                                        </small>
                                    </div>
                                </div>
                            })
                        }
                        <div className="form-group row">
                            <label className="col-2 col-form-label text-align-right" htmlFor="phoneNumber">
                                <FormattedMessage
                                    id="user.form.edit.phone_number_label"
                                    defaultMessage="Phone Number"
                                />
                            </label>
                            <div className="col-10">
                                <PhoneNumberInput
                                    name="phoneNumber"
                                    phoneNumber={this.state.phoneNumber}
                                    onChange={this.handlePhoneNumberChange}
                                    checkValidForm={this.state.checkValidForm}
                                    id="phoneNumber"
                                    type="text"
                                    className="form-control"
                                    placeholder={this.props.intl.formatMessage(messages['user.form.edit.phone_number_placeholder'])}
                                />
                                <small className="form-text text-muted">
                                    <FormattedMessage
                                        id="user.form.edit.phone_number_description"
                                        defaultMessage="Phone number of the user"
                                    />
                                </small>
                            </div>
                        </div>
                        {isAdmin ? <div className="form-group row">
                            <label className="col-2 col-form-label text-align-right" htmlFor="roleKey">
                                <FormattedMessage
                                    id="user.form.edit.role_label"
                                    defaultMessage="Role"
                                />
                            </label>
                            <div className="col-10">
                                <select
                                    className="form-control"
                                    name="roleKey"
                                    value={this.state.roleKey}
                                    onChange={this.handleFormChange}
                                    id="roleKey">
                                    <option value="">
                                        {this.props.intl.formatMessage(messages['user.form.edit.role_choose'])}
                                    </option>                                        
                                    {this.state.allRoleKeys
                                        .filter(role => role.attributes.key !== "worker")
                                        .map((role, index) => (
                                            <option key={index} value={role.attributes.key}>
                                                {this.getFormattedRoleName(role.attributes.key, messages)}
                                            </option>
                                        ))
                                    }
                                </select>
                                <small className="form-text text-muted">
                                    <FormattedMessage
                                        id="user.form.edit.role_description"
                                        defaultMessage="The role will affect the permission of the user"
                                    />
                                </small>
                            </div>
                        </div> : ''}
                        <div className="form-group row">
                            <label className="col-2 col-form-label text-align-right" htmlFor="language">
                                <FormattedMessage
                                    id="user.form.edit.language_label"
                                    defaultMessage="Language"
                                />
                            </label>
                            <div className="col-10">
                                <select
                                    className="form-control"
                                    name="language"
                                    value={this.state.language}
                                    onChange={this.handleFormChange}
                                    id="language">
                                    {GlobalHelper.getSupportedLocales().map(locale => {
                                        return <option value={locale}>
                                            {this.props.intl.formatMessage(GlobalHelper.getLanguageMessage(locale))}
                                        </option>
                                    })}
                                </select>
                                <small className="form-text text-muted">
                                    <FormattedMessage
                                        id="user.form.edit.language_description"
                                        defaultMessage="Language of the interface for the user"
                                    />
                                </small>
                            </div>
                        </div>
                        {GlobalHelper.hasAccessToAllLocations() ? <div className="form-group row">
                            <label className="col-2 col-form-label text-align-right" htmlFor="locationId">
                                <FormattedMessage
                                    id="user.form.edit.location_label"
                                    defaultMessage="Location"
                                />
                            </label>
                            <div className="col-10">
                                <LocationSelect
                                    value={this.state.locationId}
                                    onChange={locationId => { this.setState({ locationId: locationId }) }}
                                    className="form-control"
                                />
                                <small className="form-text text-muted">
                                    <FormattedMessage
                                        id="user.form.edit.location_description"
                                        defaultMessage="Users can see only the quotes and invoices from their location expect for Admins"
                                    />
                                </small>
                            </div>
                        </div> : ''}
                        {AppContext.getUserLoggedIn().attributes.superUser ? <div className="form-group row">
                            <label className="col-2 col-form-label text-align-right" htmlFor="locationId">
                                <FormattedMessage
                                    id="user.form.edit.record_in_fullstory_label"
                                    defaultMessage="Record in FullStory"
                                />
                            </label>
                            <div className="col-10">
                                <input
                                    name="recordInFullStoryUntilDate"
                                    value={this.state.recordInFullStoryUntilDate}
                                    onChange={this.handleFormChange}
                                    id="recordInFullStoryUntilDate"
                                    type="date"
                                    className="form-control"
                                />
                                <small className="form-text text-muted">
                                    <FormattedMessage
                                        id="user.form.edit.record_in_fullstory_description"
                                        defaultMessage="This will record session of this user in FullStory until that date"
                                    />
                                </small>
                            </div>
                        </div> : ''}
                        {!this.props.user &&
                            <>
                                <hr />
                                <div className="form-group row">
                                    <label className="col-2 col-form-label text-align-right" htmlFor="password">
                                        <FormattedMessage
                                            id="user.form.edit.new_password_label"
                                            defaultMessage="New Password"
                                        />
                                    </label>
                                    <div className="col-10">
                                        <PasswordInput
                                            inputName="password"
                                            handleFormChange={this.handleFormChange}
                                            value={this.state.password}
                                            placeholder={this.props.intl.formatMessage(messages['user.form.edit.password_placeholder'])}
                                        >
                                        </PasswordInput>
                                        <small className="form-text text-muted">
                                            <FormattedMessage
                                                id="user.form.edit.password_description"
                                                defaultMessage="Password of the user (leave empty if you don't want to change it)"
                                            />
                                        </small>
                                    </div>
                                </div>
                                <div className="form-group row">
                                    <label className="col-2 col-form-label text-align-right" htmlFor="passwordAgain">
                                        <FormattedMessage
                                            id="user.form.edit.password_again_label"
                                            defaultMessage="Password again"
                                        />
                                    </label>
                                    <div className="col-10">
                                        <PasswordStrengthVerificationInput
                                            inputName="passwordAgain"
                                            handleFormChange={this.handleFormChange}
                                            handlePasswordScoreChange={this.handlePasswordScoreChange}
                                            value={this.state.passwordAgain}
                                            placeHolder={this.props.intl.formatMessage(messages['user.form.edit.password_again_placeholder'])}
                                        />
                                        {this.state.passwordRules}
                                        <small className="form-text text-muted">
                                            <FormattedMessage
                                                id="user.form.edit.password_again_description"
                                                defaultMessage="Enter the password again (leave empty if you don't want to change it)"
                                            />
                                        </small>
                                    </div>
                                </div>
                                <hr />
                            </>
                        }
                        {
                            Object.keys(this.state.externalUsersByIntegrationMapping).map((integrationKey) => {
                                return this.state.externalUsersByIntegrationMapping[integrationKey].length > 0 ?
                                    <div className="form-group row align-items-center">
                                        <label className="col-2 col-form-label text-align-right" htmlFor={'externalId-' + integrationKey}>
                                            <FormattedMessage
                                                id="user.form.edit.external_id_label"
                                                defaultMessage="External user for {integrationName}"
                                                values={{
                                                    integrationName: Integrations.getIntegrationLogoImage(integrationKey, '75')
                                                }}
                                            />
                                        </label>

                                        <div className="col-10">
                                            <select
                                                id={integrationKey}
                                                name={integrationKey}
                                                className={'form-control'}
                                                value={this.state.externalIdsMapping[integrationKey]}
                                                onChange={this.handleExternalUserChange = this.handleExternalUserChange.bind(this)}
                                            >
                                                <option value={''}>
                                                    {this.props.intl.formatMessage(messages['user.form.edit.none_label'])}
                                                </option>

                                                {
                                                    this.state.externalUsersByIntegrationMapping[integrationKey].map(user => {
                                                        return <option id={user.externalId} key={user.externalId} value={user.externalId}>
                                                            {`${user.firstName} ${user.lastName}`}
                                                        </option>
                                                    })
                                                }
                                            </select>
                                        </div>
                                    </div> : null
                            })
                        }
                    </div>
                    <div className="col-12">
                        {this.state.formStatus}
                    </div>
                    <div className="col-12 ui-module">
                        <div className="row">
                            <div className="col-6">
                                {this.state.deleteButton}
                            </div>
                            <div className="col-6 text-align-right">
                                <Link to={this.state.cancelRedirectPath} className="btn btn-light">
                                    <FormattedMessage
                                        id="user.form.edit.cancel_btn"
                                        defaultMessage="Cancel"
                                    />
                                </Link>
                                &nbsp;
                                &nbsp;
                                <input
                                    type="submit"
                                    value={this.props.intl.formatMessage(messages['user.form.edit.save_btn'])}
                                    className="btn btn-primary"
                                    disabled={!this.formIsDirty(this.props.user)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </form >
        );
    }
}

export default injectIntl(UserEditForm)


// WEBPACK FOOTER //
// ./src/user/form/edit.component.jsx